import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocalizedApiError } from '@/features/audit-issues/LocalizedApiError';
import type { CorrectionRequestDto } from '@/features/governance/corrections/types';
import type { InboundPackageDto } from '@/features/operations/inbound/types';
import { invalidateInboundChainQueries } from '@/features/operations/inbound/useInbound';
import { apiClient, ApiClientError, type ApiSuccess } from '@/lib/api-client';

const NS = 'operations';
const KG_FORMAT = new Intl.NumberFormat('ja-JP', {
  maximumFractionDigits: 3,
});
const KG_INPUT_PATTERN = /^\d+(\.\d{1,3})?$/;

type WeightField = 'gross_kg' | 'tare_kg';

export interface InboundPackageWeightCorrectionDialogProps {
  batchId: string;
  pkg: InboundPackageDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applyImmediately: boolean;
}

interface FormState {
  gross: string;
  tare: string;
  reason: string;
}

interface TransitionCheckpoint {
  request: CorrectionRequestDto;
  status: 'requested' | 'approved';
  payloadKey: string;
}

const normalizeDecimal = (value: string | null | undefined): string => {
  if (value == null) return '';
  return value.trim();
};

const parseKg = (value: string): number | null => {
  const trimmed = value.trim();
  if (!KG_INPUT_PATTERN.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const sameDecimal = (left: string, right: string): boolean => {
  const leftNumber = parseKg(left);
  const rightNumber = parseKg(right);
  if (leftNumber == null || rightNumber == null) return left.trim() === right.trim();
  return leftNumber === rightNumber;
};

const formatKg = (value: string | null | undefined): string => {
  const parsed = parseKg(normalizeDecimal(value));
  if (parsed == null) return '-';
  return `${KG_FORMAT.format(parsed)} kg`;
};

const buildChangedValues = (
  pkg: InboundPackageDto,
  form: FormState,
): Partial<Record<WeightField, string>> => {
  const changes: Partial<Record<WeightField, string>> = {};
  const currentGross = normalizeDecimal(pkg.gross_kg);
  const currentTare = normalizeDecimal(pkg.tare_kg);
  const nextGross = form.gross.trim();
  const nextTare = form.tare.trim();

  if (!sameDecimal(nextGross, currentGross)) changes.gross_kg = nextGross;
  if (!sameDecimal(nextTare, currentTare)) changes.tare_kg = nextTare;

  return changes;
};

const postCorrectionRequest = async (body: {
  monthly_batch_id: string;
  target_entity_type: 'inbound_package';
  target_entity_id: string;
  new_value: Partial<Record<WeightField, string>>;
  reason: string;
}): Promise<CorrectionRequestDto> => {
  const res = await apiClient.post<ApiSuccess<CorrectionRequestDto>>('/correction-requests', body);
  return res.data.data;
};

const transitionCorrectionRequest = async (id: string, transition: 'approve' | 'apply') => {
  try {
    const res = await apiClient.post<ApiSuccess<CorrectionRequestDto>>(
      `/correction-requests/${id}/${transition}`,
    );
    return res.data.data;
  } catch (error) {
    if (!(error instanceof ApiClientError) || error.status !== 409) throw error;

    // The server may have completed the transition even when its response was
    // lost. Reconcile the persisted state before treating a retry as failed.
    const current = await apiClient.get<ApiSuccess<CorrectionRequestDto>>(
      `/correction-requests/${id}`,
    );
    const request = current.data.data;
    const transitionAlreadyCompleted =
      transition === 'approve'
        ? request.status === 'approved' || request.status === 'applied'
        : request.status === 'applied';
    if (transitionAlreadyCompleted) return request;
    throw error;
  }
};

export const InboundPackageWeightCorrectionDialog = ({
  batchId,
  pkg,
  open,
  onOpenChange,
  applyImmediately,
}: InboundPackageWeightCorrectionDialogProps) => {
  const { t } = useTranslation(NS);
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>({ gross: '', tare: '', reason: '' });
  const [result, setResult] = useState<'applied' | 'requested' | null>(null);
  const [transitionCheckpoint, setTransitionCheckpoint] = useState<TransitionCheckpoint | null>(
    null,
  );

  useEffect(() => {
    if (!open || !pkg) return;
    setForm({
      gross: normalizeDecimal(pkg.gross_kg),
      tare: normalizeDecimal(pkg.tare_kg),
      reason: '',
    });
    setResult(null);
    setTransitionCheckpoint(null);
  }, [open, pkg]);

  const gross = parseKg(form.gross);
  const tare = parseKg(form.tare);
  const newNet = gross != null && tare != null ? gross - tare : null;
  const changedValues = useMemo(() => (pkg ? buildChangedValues(pkg, form) : {}), [form, pkg]);
  const hasChanges = Object.keys(changedValues).length > 0;
  const reasonPresent = form.reason.trim().length > 0;
  const isValidWeights =
    gross != null && tare != null && gross > 0 && tare >= 0 && gross > tare && newNet != null;
  const canSubmit = Boolean(pkg && hasChanges && reasonPresent && isValidWeights && !result);
  const payloadKey = pkg
    ? JSON.stringify({
        batchId,
        packageId: pkg.inbound_package_id,
        changedValues,
        reason: form.reason.trim(),
      })
    : '';
  const correctionStarted = transitionCheckpoint != null;

  const mutation = useMutation<CorrectionRequestDto, ApiClientError>({
    mutationFn: async () => {
      if (!pkg || !canSubmit) throw new Error('Invalid correction state');
      let checkpoint =
        transitionCheckpoint?.payloadKey === payloadKey ? transitionCheckpoint : null;
      let request: CorrectionRequestDto;

      if (!checkpoint) {
        request = await postCorrectionRequest({
          monthly_batch_id: batchId,
          target_entity_type: 'inbound_package',
          target_entity_id: pkg.inbound_package_id,
          new_value: changedValues,
          reason: form.reason.trim(),
        });
        checkpoint = { request, status: 'requested', payloadKey };
        setTransitionCheckpoint(checkpoint);
      } else {
        request = checkpoint.request;
      }

      if (!applyImmediately) return request;
      if (checkpoint.status === 'requested') {
        const approved = await transitionCorrectionRequest(checkpoint.request.id, 'approve');
        if (approved.status === 'applied') return approved;
        checkpoint = { ...checkpoint, request: approved, status: 'approved' };
        setTransitionCheckpoint(checkpoint);
        request = approved;
      }
      return transitionCorrectionRequest(request.id, 'apply');
    },
    onSuccess: async () => {
      invalidateInboundChainQueries(queryClient);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['inbound-lots'] }),
        queryClient.invalidateQueries({ queryKey: ['correction-requests', 'list'] }),
      ]);
      setTransitionCheckpoint(null);
      setResult(applyImmediately ? 'applied' : 'requested');
    },
  });

  const updateForm = (patch: Partial<FormState>) => {
    setResult(null);
    setForm((prev) => ({ ...prev, ...patch }));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          mutation.reset();
          setResult(null);
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('efs.operations.inbound.weight_correction.title', {
              defaultValue: '重量を強制修正',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('efs.operations.inbound.weight_correction.description', {
              defaultValue:
                '確定済みの入荷包装重量を修正申請として記録します。理由は監査履歴に残ります。',
            })}
          </DialogDescription>
        </DialogHeader>

        {pkg ? (
          <div className="space-y-4">
            <div className="rounded-md border border-efs-border bg-efs-surface-muted p-3">
              <p className="text-xs text-efs-text-muted">
                {t('efs.operations.inbound.weight_correction.package_code', {
                  defaultValue: '包装コード',
                })}
              </p>
              <p className="font-mono text-sm font-semibold">{pkg.package_code}</p>
              <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
                <div>
                  <dt className="text-xs text-efs-text-muted">
                    {t('efs.operations.inbound.fields.gross_kg', { defaultValue: '総重量' })}
                  </dt>
                  <dd className="font-mono tabular-nums">{formatKg(pkg.gross_kg)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-efs-text-muted">
                    {t('efs.operations.inbound.fields.tare_kg', { defaultValue: '風袋' })}
                  </dt>
                  <dd className="font-mono tabular-nums">{formatKg(pkg.tare_kg)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-efs-text-muted">
                    {t('efs.operations.inbound.fields.net_kg', { defaultValue: '正味' })}
                  </dt>
                  <dd className="font-mono tabular-nums">{formatKg(pkg.net_kg)}</dd>
                </div>
              </dl>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="inbound-weight-correction-gross">
                  {t('efs.operations.inbound.weight_correction.new_gross', {
                    defaultValue: '新しい総重量 (kg)',
                  })}
                </Label>
                <Input
                  id="inbound-weight-correction-gross"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.001"
                  value={form.gross}
                  onChange={(event) => updateForm({ gross: event.target.value })}
                  disabled={mutation.isPending || correctionStarted || result != null}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inbound-weight-correction-tare">
                  {t('efs.operations.inbound.weight_correction.new_tare', {
                    defaultValue: '新しい風袋 (kg)',
                  })}
                </Label>
                <Input
                  id="inbound-weight-correction-tare"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.001"
                  value={form.tare}
                  onChange={(event) => updateForm({ tare: event.target.value })}
                  disabled={mutation.isPending || correctionStarted || result != null}
                />
              </div>
            </div>

            <div className="rounded-md border border-efs-border p-3">
              <p className="text-xs text-efs-text-muted">
                {t('efs.operations.inbound.weight_correction.new_net', {
                  defaultValue: '修正後の正味',
                })}
              </p>
              <p className="font-mono text-lg font-semibold tabular-nums">
                {newNet != null ? `${KG_FORMAT.format(newNet)} kg` : '-'}
              </p>
              {!isValidWeights ? (
                <p className="mt-1 text-xs text-destructive" role="alert">
                  {t('efs.operations.inbound.weight_correction.weight_error', {
                    defaultValue: '総重量は0より大きく、風袋以上で入力してください。',
                  })}
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inbound-weight-correction-reason">
                {t('efs.operations.inbound.weight_correction.reason', {
                  defaultValue: '修正理由',
                })}
              </Label>
              <Textarea
                id="inbound-weight-correction-reason"
                value={form.reason}
                rows={3}
                maxLength={2000}
                onChange={(event) => updateForm({ reason: event.target.value })}
                disabled={mutation.isPending || correctionStarted || result != null}
                placeholder={t('efs.operations.inbound.weight_correction.reason_placeholder', {
                  defaultValue: '例：風袋重量を20kgではなく23kgとして入力していたため',
                })}
              />
            </div>

            {!hasChanges ? (
              <p className="text-xs text-efs-text-muted" role="status">
                {t('efs.operations.inbound.weight_correction.no_change', {
                  defaultValue: '変更された重量がありません。',
                })}
              </p>
            ) : null}

            {result ? (
              <p className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2 text-sm text-emerald-900 dark:text-emerald-100">
                {result === 'applied'
                  ? t('efs.operations.inbound.weight_correction.applied', {
                      defaultValue: '修正を適用しました。',
                    })
                  : t('efs.operations.inbound.weight_correction.requested', {
                      defaultValue: '修正申請を作成しました。承認待ちです。',
                    })}
              </p>
            ) : null}

            {mutation.error ? (
              <LocalizedApiError error={mutation.error} showTechnicalDetails={false} />
            ) : null}

            {correctionStarted && !result ? (
              <p className="text-xs text-efs-text-muted" role="status">
                {t('efs.operations.inbound.weight_correction.resume_hint', {
                  defaultValue: '修正申請は作成済みです。再試行すると同じ申請の処理を続行します。',
                })}
              </p>
            ) : null}
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
          >
            {mutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {applyImmediately
              ? t('efs.operations.inbound.weight_correction.apply_now', {
                  defaultValue: '修正を適用',
                })
              : t('efs.operations.inbound.weight_correction.create_request', {
                  defaultValue: '修正申請を作成',
                })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
